import clsx from 'clsx';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';

/**
 * A dropdown component (presentational). Used with quantity and options.
 * @param {boolean} skeleton - If true, renders a skeleton version of the dropdown for Suspense fallback.
 * @param {string} dataCy - `data-cy` for the real control.
 */
const OptionDropdown = ({
  id,
  name,
  options,
  value,
  onChange,
  className,
  skeleton = false,
  dataCy = 'option-dropdown'
}) => {
  return (
    <select
      id={id}
      name={name}
      className={clsx(
        'body-r focus-visible:ring-blue mt-2 block cursor-pointer truncate bg-white py-3 pr-3 pl-5 text-black focus-visible:border focus-visible:border-black focus-visible:ring-4',
        className
      )}
      value={value}
      onChange={onChange}
      disabled={skeleton}
      data-cy={skeleton ? undefined : dataCy}
      {...(skeleton ? SKELETON_PROPS : null)}
    >
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          aria-disabled={option.disabled}
          className={clsx(
            'p-4 text-black',
            option.value === value ? 'bg-warm-pewter-80' : 'bg-white'
          )}
        >
          {option.label || option.value}
        </option>
      ))}
    </select>
  );
};

export default OptionDropdown;

// `\u00A0` is a non-breaking space, used to keep the placeholder option blank.
const SKELETON_OPTIONS = [{ value: '', label: '\u00A0' }];

/**
 * Suspense fallback for any `<OptionDropdown>`: the same control with a single
 * blank option.
 *
 * Pass the same `className` the real dropdown gets, so the two can't drift.
 */
export const DropdownSkeleton = ({ className }) => (
  <OptionDropdown
    options={SKELETON_OPTIONS}
    value=""
    className={className}
    skeleton
  />
);
